import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import StatusBadge, { STATUS_STYLES } from "../../components/StatusBadge";
import { ArrowRight, Plane, FilePlus, Search } from "lucide-react";

const STATUS_FILTERS = ["All", "Open", "Under Review", "Quoted", "Closed"];

const RfqList = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rfqs, setRfqs] = useState([]);
    const [filter, setFilter] = useState("All");
    const [q, setQ] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/rfq");
                setRfqs(data);
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = rfqs
        .filter((r) => filter === "All" || r.status === filter)
        .filter((r) =>
            q.trim() === ""
                ? true
                : (r.ticket_number + " " + (r.client_name || "") + " " + (r.client_company || ""))
                      .toLowerCase()
                      .includes(q.trim().toLowerCase())
        );

    return (
        <div data-testid="rfq-list-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div
                        className="font-body text-[11px] uppercase tracking-[0.22em] mb-2"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        {isAdmin ? "All requests across the network" : "Your submitted requests"}
                    </div>
                    <h1
                        className="font-display text-3xl lg:text-4xl font-bold tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        RFQs
                    </h1>
                </div>
                {!isAdmin && (
                    <Link
                        to="/portal/rfqs/new"
                        className="ns-btn-primary"
                        data-testid="rfqlist-new"
                    >
                        <FilePlus size={16} /> New RFQ
                    </Link>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex flex-wrap gap-1.5">
                    {STATUS_FILTERS.map((s) => {
                        const active = filter === s;
                        return (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setFilter(s)}
                                data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`px-3 py-2 font-body text-[12px] uppercase tracking-[0.14em] border transition-colors ${
                                    active
                                        ? "bg-[var(--ns-ink)] text-white border-[var(--ns-ink)]"
                                        : "bg-white border-[var(--ns-border)] hover:border-[var(--ns-ink)]"
                                }`}
                                style={!active ? { color: "var(--ns-body)" } : {}}
                            >
                                {s}
                            </button>
                        );
                    })}
                </div>
                <div className="relative">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2"
                        style={{ color: "var(--ns-muted-2)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search ticket, client…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 pr-3 py-2 bg-white border border-[var(--ns-border)] font-body text-sm w-[240px] focus:outline-none focus:border-[var(--ns-ink)]"
                        data-testid="rfqlist-search"
                    />
                </div>
            </div>

            {err && (
                <div className="mb-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm" style={{ color: "#7a2218" }}>
                    {err}
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-[var(--ns-border)] overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-[var(--ns-border)]" style={{ background: "#fafaf8" }}>
                            <Th>Ticket</Th>
                            <Th>Aircraft</Th>
                            {isAdmin && <Th>Client</Th>}
                            <Th>Date</Th>
                            <Th>Lines</Th>
                            <Th>Status</Th>
                            <Th></Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={isAdmin ? 7 : 6} className="px-6 py-16 text-center font-body text-sm" style={{ color: "var(--ns-muted)" }}>
                                    Loading…
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={isAdmin ? 7 : 6} className="px-6 py-16 text-center font-body text-sm" style={{ color: "var(--ns-muted)" }}>
                                    No requests match the current filters.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((r) => (
                                <tr
                                    key={r.id}
                                    className="border-b border-[var(--ns-border)] last:border-0 hover:bg-[#fbfaf7]"
                                    data-testid={`rfq-row-${r.ticket_number}`}
                                >
                                    <Td>
                                        <span className="font-display font-semibold tabular-nums" style={{ color: "var(--ns-ink)" }}>
                                            {r.ticket_number}
                                        </span>
                                    </Td>
                                    <Td>
                                        <span className="inline-flex items-center gap-2 font-body text-sm">
                                            <Plane size={14} style={{ color: "var(--ns-muted)" }} />
                                            {r.aircraft_type}
                                        </span>
                                    </Td>
                                    {isAdmin && (
                                        <Td>
                                            <div className="font-body text-sm" style={{ color: "var(--ns-ink)" }}>{r.client_name || "—"}</div>
                                            {r.client_company && <div className="font-body text-xs" style={{ color: "var(--ns-muted)" }}>{r.client_company}</div>}
                                        </Td>
                                    )}
                                    <Td>
                                        <span className="font-body text-sm tabular-nums" style={{ color: "var(--ns-muted)" }}>
                                            {r.creation_date?.slice(0, 10)}
                                        </span>
                                    </Td>
                                    <Td>
                                        <span className="font-body text-sm tabular-nums" style={{ color: "var(--ns-muted)" }}>{r.items.length}</span>
                                    </Td>
                                    <Td>
                                        <StatusBadge status={r.status} size="sm" />
                                    </Td>
                                    <Td>
                                        <Link to={`/portal/rfqs/${r.id}`} className="font-body text-sm font-medium inline-flex items-center gap-1 hover:underline" style={{ color: "var(--ns-ink)" }}>
                                            Open <ArrowRight size={13} />
                                        </Link>
                                    </Td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Th = ({ children }) => (
    <th className="text-left px-6 py-3.5 font-body text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: "var(--ns-muted)" }}>
        {children}
    </th>
);
const Td = ({ children }) => <td className="px-6 py-4">{children}</td>;

export default RfqList;
